import { useCallback, useMemo, useState } from 'react';
import { DbIdsPerModel } from '../../../common/ForgeViewer';
import {
  HatchPatternConfig,
  HatchPatternType,
} from 'src/components/viewers/SheetsViewerV2/shapes/utils/hatch-patterns';

export type { HatchPatternConfig, HatchPatternType };

export type SheetsColorizationManager = {
  colorization: {
    colorFunc: (urn: string, dbId: number) => string;
    dbIdsPerModel: DbIdsPerModel;
  } | null;
  colorSection: (
    colorFunc: (urn: string, dbId: number) => string,
    dbIdsPerModel: DbIdsPerModel
  ) => void;
  clearColors: () => void;
  /** Pattern configuration for sheet polygon fills */
  pattern: HatchPatternConfig | null;
  /** Set a pattern to apply to all sheet polygons */
  setPattern: (pattern: HatchPatternConfig | null) => void;
  /** Clear the pattern */
  clearPattern: () => void;
};

/**
 * Hook for managing colorization and patterns of elements in the Sheet viewer.
 * If you want colorization for all elements, including sheet elements, consider [colorization.ts](../../services/viewer/colorization.ts)
 */
export const useSheetsColorization = (): SheetsColorizationManager => {
  const [colorization, setColorization] = useState<{
    colorFunc: (urn: string, dbId: number) => string;
    dbIdsPerModel: DbIdsPerModel;
  } | null>(null);

  const [pattern, setPatternState] = useState<HatchPatternConfig | null>(null);

  const colorSection = useCallback(
    (
      colorFunc: (urn: string, dbId: number) => string,
      dbIdsPerModel: DbIdsPerModel
    ) => {
      setColorization({
        colorFunc,
        dbIdsPerModel,
      });
    },
    []
  );

  const clearColors = useCallback(() => {
    setColorization(null);
  }, []);

  const setPattern = useCallback((newPattern: HatchPatternConfig | null) => {
    setPatternState(newPattern);
  }, []);

  const clearPattern = useCallback(() => {
    setPatternState(null);
  }, []);

  return useMemo(
    () => ({
      colorization,
      colorSection,
      clearColors,
      pattern,
      setPattern,
      clearPattern,
    }),
    [clearColors, colorSection, colorization, pattern, setPattern, clearPattern]
  );
};
